import { consoleApiSlice } from "./consoleApiSlice";

export const notificationsApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // PRO-133: send (or resend with override) a run-announcement email via
    // Console Backend thin proxy → Platform notify API.
    sendRunAnnouncement: builder.mutation({
      query: ({
        scanId,
        runId,
        useProd = false,
        override = false,
        dryRun = false,
        firePath = "console_manual",
        recipientEmails,
      }) => ({
        url: `/api/v1/notifications/run-announcement`,
        method: "POST",
        body: {
          scan_id: scanId || null,
          run_id: runId || null,
          use_prod: useProd,
          override,
          dry_run: dryRun,
          fire_path: firePath,
          ...(recipientEmails !== undefined
            ? { recipient_emails: recipientEmails }
            : {}),
        },
      }),
      // A live send adds a Send Details row; refetch it.
      invalidatesTags: (result, error, arg) => (arg?.dryRun ? [] : ["CustomerEvents"]),
    }),
    // agentSlug / runId preselect the run's per-agent recipient config
    // (CUS-1353). Without them Platform preselects the website default.
    // Console Backend's /api/v1/notifications/recipient-options forwards only
    // website_id, so this goes through the generic Platform proxy instead.
    getNotificationRecipientOptions: builder.query({
      query: ({ websiteId, useProd = true, agentSlug, runId }) => ({
        url: `/platform-proxy/api/v2/notifications/recipient-options`,
        params: {
          website_id: websiteId,
          use_prod: useProd,
          ...(agentSlug ? { agent_slug: agentSlug } : {}),
          ...(runId ? { run_id: runId } : {}),
        },
      }),
    }),
    // A Platform user, for the email of a staging run's launcher: Platform
    // adds a run's launcher itself only for runs on the env it serves.
    getPlatformUser: builder.query({
      query: ({ userId, useProd = false }) => ({
        url: `/platform-proxy/api/v1/users/${encodeURIComponent(userId)}`,
        params: { use_prod: useProd },
      }),
    }),
    // Status for Console gray-out of the Send notification button.
    getRunAnnouncementStatusByScan: builder.query({
      query: ({ scanId, useProd = false }) => ({
        url: `/api/v1/notifications/run-announcement/by-scan/${scanId}`,
        params: { use_prod: useProd },
      }),
    }),
    // PRO-133 phase 3: build-and-store-once artifact preview. Never sends;
    // lets the Console preview modal render the exact subject/preheader/HTML
    // a subsequent approved send would deliver.
    prepareRunAnnouncement: builder.mutation({
      query: ({
        scanId,
        runId,
        useProd = false,
        firePath = "console_manual",
        triggeredBy,
        forceRegenerate = false,
      }) => ({
        url: `/api/v1/notifications/run-announcement/prepare`,
        method: "POST",
        body: {
          scan_id: scanId || null,
          run_id: runId || null,
          use_prod: useProd,
          fire_path: firePath,
          ...(triggeredBy !== undefined ? { triggered_by: triggeredBy } : {}),
          force_regenerate: forceRegenerate,
        },
      }),
    }),
    // Send the canonical artifact. Passing expected_artifact_version +
    // expected_content_hash together makes this an *approved* send: Platform
    // fails closed (rather than regenerating) if the stored artifact no
    // longer matches what the operator reviewed.
    sendPreparedRunAnnouncement: builder.mutation({
      query: ({
        scanId,
        runId,
        useProd = false,
        override = false,
        dryRun = false,
        firePath = "console_manual",
        triggeredBy,
        recipientEmails,
        expectedArtifactVersion,
        expectedContentHash,
      }) => ({
        url: `/api/v1/notifications/run-announcement/send`,
        method: "POST",
        body: {
          scan_id: scanId || null,
          run_id: runId || null,
          use_prod: useProd,
          override,
          dry_run: dryRun,
          fire_path: firePath,
          ...(triggeredBy !== undefined ? { triggered_by: triggeredBy } : {}),
          ...(recipientEmails !== undefined
            ? { recipient_emails: recipientEmails }
            : {}),
          ...(expectedArtifactVersion !== undefined
            ? { expected_artifact_version: expectedArtifactVersion }
            : {}),
          ...(expectedContentHash !== undefined
            ? { expected_content_hash: expectedContentHash }
            : {}),
        },
      }),
      invalidatesTags: (result, error, arg) => (arg?.dryRun ? [] : ["CustomerEvents"]),
    }),
  }),
});

export const {
  useSendRunAnnouncementMutation,
  useGetNotificationRecipientOptionsQuery,
  useGetPlatformUserQuery,
  useGetRunAnnouncementStatusByScanQuery,
  usePrepareRunAnnouncementMutation,
  useSendPreparedRunAnnouncementMutation,
} = notificationsApiSlice;
